// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {guessPlatform, isChromiumBrowser, isDesktop, isFirefoxBrowser} from '@app/features/ui/utils/NativeUtils';
import {getGpuEncoderReportSync, type HardwareEncodeAnswer} from '@app/features/voice/utils/GpuEncoderCapabilities';
import type {VideoCodec} from 'livekit-client';

const logger = new Logger('CodecCapabilityDetector');
export const LIVEKIT_SUPPORTED_CODECS: ReadonlyArray<VideoCodec> = ['vp8', 'h264', 'vp9', 'av1', 'h265'];
export interface CodecCapabilities {
	vp8: boolean;
	vp9: boolean;
	h264: boolean;
	h265: boolean;
	av1: boolean;
}
export type CodecSupportReason =
	| 'supported'
	| 'unsupported-browser'
	| 'unsupported-system'
	| 'unavailable-on-platform'
	| 'capabilities-unavailable';
export interface CodecSupportInfo {
	supported: boolean;
	reason: CodecSupportReason;
	detail: string;
	hardwareAccelerated: HardwareEncodeAnswer;
}
export interface CodecCapabilityReport {
	vp8: CodecSupportInfo;
	vp9: CodecSupportInfo;
	h264: CodecSupportInfo;
	h265: CodecSupportInfo;
	av1: CodecSupportInfo;
}
export type CodecPreference = 'auto' | VideoCodec;
export type ScreenShareContentHint = 'auto' | 'detail' | 'motion' | 'text';
let cachedCapabilities: CodecCapabilities | null = null;
let cachedReport: CodecCapabilityReport | null = null;
let cachedReportGpuKey: object | null | undefined;
interface RawProbeResult {
	caps: CodecCapabilities;
	probedSuccessfully: boolean;
}
function probeRawCapabilities(): RawProbeResult {
	const caps: CodecCapabilities = {
		vp8: false,
		vp9: false,
		h264: false,
		h265: false,
		av1: false,
	};
	try {
		const capabilities = RTCRtpSender.getCapabilities?.('video');
		if (!capabilities) {
			logger.debug('RTCRtpSender.getCapabilities not available; assuming baseline codec support only');
			caps.vp8 = true;
			caps.h264 = true;
			return {caps, probedSuccessfully: false};
		}
		const mimeTypes = new Set(capabilities.codecs.map((c) => c.mimeType.toLowerCase()));
		caps.vp8 = mimeTypes.has('video/vp8');
		caps.vp9 = mimeTypes.has('video/vp9');
		caps.h264 = mimeTypes.has('video/h264');
		caps.h265 = mimeTypes.has('video/h265');
		caps.av1 = mimeTypes.has('video/av1');
		logger.info('Codec capabilities probed', {capabilities: caps});
		return {caps, probedSuccessfully: true};
	} catch (error) {
		logger.warn('Failed to probe codec capabilities, assuming VP8/H.264 only', {error});
		caps.vp8 = true;
		caps.h264 = true;
		return {caps, probedSuccessfully: false};
	}
}
function probeEncodingCapabilities(): CodecCapabilities {
	if (cachedCapabilities) return cachedCapabilities;
	const {caps} = probeRawCapabilities();
	cachedCapabilities = caps;
	return caps;
}
function buildReport(): CodecCapabilityReport {
	const {caps, probedSuccessfully} = probeRawCapabilities();
	const platform = guessPlatform();
	const firefox = isFirefoxBrowser();
	const chromium = isChromiumBrowser();
	const gpuReport = getGpuEncoderReportSync();
	function hwAccel(codec: keyof CodecCapabilities): HardwareEncodeAnswer {
		return gpuReport ? gpuReport[codec] : 'unknown';
	}
	type DescribedUnsupported = Omit<CodecSupportInfo, 'hardwareAccelerated'>;
	function unsupported(codec: keyof CodecCapabilities, info: DescribedUnsupported): CodecSupportInfo {
		return {...info, hardwareAccelerated: hwAccel(codec)};
	}
	function describe(codec: keyof CodecCapabilities): CodecSupportInfo {
		if (caps[codec]) {
			return {
				supported: true,
				reason: 'supported',
				detail: 'Available on your system.',
				hardwareAccelerated: hwAccel(codec),
			};
		}
		if (!probedSuccessfully) {
			return unsupported(codec, {
				supported: false,
				reason: 'capabilities-unavailable',
				detail:
					'WebRTC capability probing is unavailable in this browser; only baseline VP8/H.264 are assumed available.',
			});
		}
		if (firefox) {
			switch (codec) {
				case 'av1':
					return unsupported(codec, {
						supported: false,
						reason: 'unsupported-browser',
						detail: 'Firefox doesn\u2019t support AV1 encoding for WebRTC yet.',
					});
				case 'vp9':
					return unsupported(codec, {
						supported: false,
						reason: 'unsupported-browser',
						detail: 'Firefox\u2019s WebRTC stack doesn\u2019t expose VP9 as a publishable codec.',
					});
				case 'h265':
					return unsupported(codec, {
						supported: false,
						reason: 'unsupported-browser',
						detail: 'Firefox doesn\u2019t support H.265 encoding for WebRTC.',
					});
				default:
					break;
			}
		}
		if (codec === 'av1' && isDesktop() && platform === 'linux') {
			return unsupported(codec, {
				supported: false,
				reason: 'unavailable-on-platform',
				detail:
					'AV1 publishing is disabled on Linux desktop builds because most Electron-bundled Chromium versions lack a stable hardware AV1 encoder there, and software AV1 saturates the CPU during screen sharing.',
			});
		}
		if (codec === 'h265' && isDesktop() && platform === 'linux') {
			return unsupported(codec, {
				supported: false,
				reason: 'unavailable-on-platform',
				detail:
					'H.265 publishing is disabled on Linux desktop builds: Chromium has no HEVC hardware encoder backend on Linux for any GPU vendor, and software HEVC encode isn\u2019t exposed.',
			});
		}
		if (codec === 'h265') {
			return unsupported(codec, {
				supported: false,
				reason: 'unsupported-system',
				detail: chromium
					? 'Your browser/system doesn\u2019t expose an H.265 (HEVC) encoder. H.265 needs hardware support on most platforms.'
					: 'H.265 (HEVC) encoding requires a Chromium-based browser with hardware HEVC support.',
			});
		}
		if (codec === 'av1') {
			return unsupported(codec, {
				supported: false,
				reason: 'unsupported-system',
				detail:
					'Your browser/system doesn\u2019t expose an AV1 encoder. AV1 needs hardware support or recent Chromium with software fallback.',
			});
		}
		if (codec === 'vp9') {
			return unsupported(codec, {
				supported: false,
				reason: 'unsupported-system',
				detail: 'Your browser/system doesn\u2019t expose a VP9 encoder.',
			});
		}
		return unsupported(codec, {
			supported: false,
			reason: 'unsupported-system',
			detail: `Your browser/system doesn\u2019t expose a ${codec.toUpperCase()} encoder.`,
		});
	}
	return {
		vp8: describe('vp8'),
		vp9: describe('vp9'),
		h264: describe('h264'),
		h265: describe('h265'),
		av1: describe('av1'),
	};
}
export function getCodecCapabilities(): CodecCapabilities {
	return probeEncodingCapabilities();
}
export function getCodecCapabilityReport(): CodecCapabilityReport {
	const currentGpu = getGpuEncoderReportSync();
	if (cachedReport && cachedReportGpuKey === currentGpu) return cachedReport;
	cachedReport = buildReport();
	cachedReportGpuKey = currentGpu;
	return cachedReport;
}
export function getLiveKitSupportedCodecs(): ReadonlyArray<VideoCodec> {
	return LIVEKIT_SUPPORTED_CODECS;
}
export function isCodecLiveKitSupported(codec: string): codec is VideoCodec {
	return (LIVEKIT_SUPPORTED_CODECS as ReadonlyArray<string>).includes(codec);
}
export function isH265EncodingSupported(): boolean {
	return probeEncodingCapabilities().h265;
}
export function isVP9EncodingSupported(): boolean {
	return probeEncodingCapabilities().vp9;
}
export function isAV1EncodingSupported(): boolean {
	return probeEncodingCapabilities().av1;
}
export function selectOptimalCameraCodec(preference: CodecPreference = 'auto'): VideoCodec {
	if (preference !== 'auto') {
		const caps = probeEncodingCapabilities();
		if (caps[preference]) return preference;
		logger.warn('Preferred camera codec not supported, falling back to auto', {preference});
	}
	const caps = probeEncodingCapabilities();
	const platform = guessPlatform();
	if (isFirefoxBrowser()) {
		return 'vp8';
	}
	if (!isChromiumBrowser() && !isDesktop()) {
		if (caps.h264) return 'h264';
		if (caps.vp8) return 'vp8';
		return 'h264';
	}
	if (caps.vp9) {
		return 'vp9';
	}
	if (caps.h264 && platform === 'windows') return 'h264';
	return 'vp8';
}
export function selectOptimalScreenShareCodec(preference: CodecPreference = 'auto'): VideoCodec {
	if (preference !== 'auto') {
		const caps = probeEncodingCapabilities();
		if (caps[preference]) return preference;
		logger.warn('Preferred screen share codec not supported, falling back to auto', {preference});
	}
	const caps = probeEncodingCapabilities();
	const platform = guessPlatform();
	const gpu = getGpuEncoderReportSync();
	if (isFirefoxBrowser()) {
		return 'vp8';
	}
	if (!isChromiumBrowser() && !isDesktop()) {
		if (caps.h264) return 'h264';
		return 'vp8';
	}
	if (isDesktop() && caps.av1 && platform !== 'linux') {
		const av1Hw = gpu?.av1 ?? 'unknown';
		if (av1Hw === 'hardware' || av1Hw === 'unknown') {
			return 'av1';
		}
	}
	if (caps.h264 && platform === 'windows' && gpu?.vp9 === 'software' && gpu.h264 === 'hardware') {
		return 'h264';
	}
	if (caps.vp9) {
		return 'vp9';
	}
	if (caps.h264 && platform === 'windows') {
		return 'h264';
	}
	return 'vp8';
}
export function resolveScreenShareContentHint(
	preference: ScreenShareContentHint = 'auto',
): 'detail' | 'text' | 'motion' {
	if (preference === 'text' || preference === 'detail' || preference === 'motion') return preference;
	return 'detail';
}
export function getBackupCodecForPrimary(primaryCodec: VideoCodec):
	| false
	| {
			codec: 'vp8' | 'h264';
	  } {
	switch (primaryCodec) {
		case 'vp8':
			return false;
		case 'h264':
			return {codec: 'vp8'};
		case 'vp9':
		case 'av1':
		case 'h265':
			return {codec: 'vp8'};
		default:
			return {codec: 'vp8'};
	}
}
export function resetCachedCodecCapabilities(): void {
	cachedCapabilities = null;
	cachedReport = null;
}
